import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { BracketBoard } from '@/components/bracket/BracketBoard';
import { buildBracketModel } from '@/lib/bracket/model';
import {
  EMPTY_CUSTOM, applyCustomResults, bothNamed, formatEventDate, loadCustom, saveCustom, startingSlots,
} from '@/lib/bracket/custom';
import { authorizeCustomExport, trackBracket } from '@/lib/bracket/customTracking';
import { startConversion } from '@/lib/bracket/convert';
import { CustomizePanel } from './CustomizePanel';
import { SlotEditor, slotLabel } from './SlotEditor';
import { UpgradePrompt } from './UpgradePrompt';
import { ShareDialog } from './ShareDialog';
import { TeamListMenu } from './TeamListMenu';
import { SaveListDialog } from './SaveListDialog';
import { ManagedBracket } from './ManagedBracket';
import { useShareSync } from './useShareSync';

export const RETURN_KEY = 'sg_bracket_return';

// Bracket Library board with the Customize Bracket editor (preview free, print/download Pro).
export const LibraryBracket = React.forwardRef(({ template, preview, fmt, initialShareId, openShare }, boardRef) => {
  const navigate = useNavigate();
  const { user, isPro: pro, loading: authLoading } = useAuth();
  // Until auth resolves, treat as Pro so Pro users never flash the watermark; the export gate still checks the server.
  const isPro = authLoading || !!pro;
  const [c, setC] = useState(() => {
    const saved = loadCustom(template.id);
    return initialShareId || openShare ? { ...saved, active: true } : saved;
  });
  const [slot, setSlot] = useState(null);
  const [prompt, setPrompt] = useState(null);
  const [shareOpen, setShareOpen] = useState(!!openShare);
  const [saveOpen, setSaveOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [blankBusy, setBlankBusy] = useState(false);
  useEffect(() => { saveCustom(template.id, c); }, [template.id, c]);
  const sync = useShareSync({ templateId: template.id, c, setC, settings, initialShareId });

  const set = (patch) => setC((prev) => ({ ...prev, ...patch }));
  const setName = (key, v) => setC((prev) => ({ ...prev, names: { ...prev.names, [key]: v } }));
  const slots = useMemo(() => startingSlots(preview.teams), [preview.teams]);
  const teamLabels = useMemo(() => Object.fromEntries(slots.map((s) => [s.key, s.label])), [slots]);
  const labelOf = (k) => slotLabel(k, teamLabels);

  const baseTitle = `${preview.num_teams} TEAM ${fmt.label.toUpperCase()} BRACKET`;
  const baseSub = `${preview.total_matches} games  ·  Printable bracket template`;
  const schedule = useMemo(() => (c.active ? applyCustomResults(preview.schedule, c.names, c.scores) : preview.schedule), [c.active, c.names, c.scores, preview.schedule]);
  const model = useMemo(() => buildBracketModel({ format: preview.format, schedule }), [preview.format, schedule]);
  const title = c.active ? (c.title.trim() || baseTitle) : baseTitle;
  const subtitle = c.active ? [formatEventDate(c.date), `${preview.num_teams} teams`, fmt.label].filter(Boolean).join('  ·  ') : baseSub;

  const showPrompt = (kind) => { setPrompt(kind); trackBracket('upgrade_prompt', template.id); };
  const custom = useMemo(() => {
    if (!c.active) return null;
    return {
      writeIn: true,
      champNames: c.names,
      onSlot: (key) => setSlot(key),
      watermark: !isPro,
      canEdit: bothNamed,
      onLockedEdit: isPro ? undefined : () => showPrompt('scores'),
      printFallback: isPro ? null : { title: baseTitle, subtitle: baseSub, eventLogo: null },
      onBlockedPrint: () => showPrompt('export'),
      gate: async () => {
        trackBracket('customize_completed', template.id);
        if (!isPro) { showPrompt('export'); return false; }
        if (await authorizeCustomExport()) return true;
        if (!pro) { showPrompt('export'); return false; }
        toast.error('We could not confirm your Pro plan', { description: 'Sign out and back in, then try again.' });
        return false;
      },
    };
  }, [c.active, c.names, isPro, pro, template.id, baseTitle, baseSub]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = () => { set({ active: true }); trackBracket('customize_started', template.id); };
  const upgrade = () => {
    trackBracket('upgrade_click', template.id);
    try { localStorage.setItem(RETURN_KEY, template.id); } catch { /* ignore */ }
    navigate('/pricing?from=bracket-library');
  };
  const downloadBlank = async () => {
    setBlankBusy(true);
    await boardRef.current?.downloadBlank();
    setBlankBusy(false);
    setPrompt(null);
  };
  const withUndo = (message, prev, description) => toast(message, { description, action: { label: 'Undo', onClick: () => setC(prev) } });
  const clearAll = () => {
    setC({ ...EMPTY_CUSTOM, active: true, shareId: c.shareId, sourceId: c.sourceId });
    withUndo('Bracket cleared', c);
  };
  const saveScore = (matchId, a, b) => {
    const label = model.matches.find((m) => m.id === matchId)?.match_label;
    if (!label) return false;
    setC((prev) => ({ ...prev, scores: { ...prev.scores, [label]: { a, b } } }));
    if (a === b) toast.warning('Tied score saved. Nobody advances until there is a winner.');
    return true;
  };
  const manage = () => {
    if (!isPro) { showPrompt('manage'); return; }
    const sourceId = startConversion({ templateId: template.id, preview, c, title: baseTitle });
    saveCustom(template.id, { ...c, sourceId });
    navigate('/create?from=bracket');
  };
  const unlink = () => { setC({ ...EMPTY_CUSTOM, active: true }); toast('Started a new bracket', { description: 'Your tournament is still in My Schedules.' }); };
  const fillTeams = (teams, from) => {
    const names = { ...c.names };
    slots.forEach((s, i) => { if (teams[i]) names[s.key] = teams[i]; else delete names[s.key]; });
    setC({ ...c, names });
    const extra = teams.length - slots.length;
    withUndo(`Filled ${Math.min(teams.length, slots.length)} of ${slots.length} spots from ${from}`, c,
      extra > 0 ? `${extra} more team${extra > 1 ? 's' : ''} didn't fit this bracket.` : undefined);
  };
  const typedTeams = slots.map((s) => (c.names[s.key] || '').trim()).filter(Boolean);
  const teamTools = <TeamListMenu isPro={isPro} onLocked={() => showPrompt('lists')} onFill={fillTeams} onSave={() => setSaveOpen(true)} />;

  const board = (
    <BracketBoard ref={boardRef} model={model} title={title} subtitle={subtitle} eventLogo={c.active ? c.logo || null : null} logos={{}}
      storageKey="bracket-layout:library" fileName={`${preview.num_teams}-team-${fmt.short.toLowerCase().replace(/\s/g, '-')}-bracket`}
      allowBlank custom={custom} onCustomize={start} onSettingsChange={setSettings} editable={c.active && isPro} onScoreSave={saveScore} />
  );

  const editor = c.eventId ? (
    <ManagedBracket ref={boardRef} eventId={c.eventId} isPro={isPro} shared={!!sync.share} onShare={() => setShareOpen(true)} onUnlink={unlink} />
  ) : c.active ? (
    <div className="grid grid-cols-1 xl:grid-cols-[340px_minmax(0,1fr)] gap-5 items-start">
      <CustomizePanel c={c} set={set} setName={setName} slots={slots} labelOf={labelOf} isPro={isPro} teamTools={teamTools}
        onShare={() => setShareOpen(true)} shared={!!sync.share} syncStatus={sync.status} onManage={manage}
        onExport={(kind) => boardRef.current?.run(kind)} onExit={() => { setSlot(null); set({ active: false }); }} onClearAll={clearAll} />
      <div className="min-w-0">{board}</div>
    </div>
  ) : board;

  return (
    <>
      {editor}
      {c.active && !c.eventId && slot && (
        <SlotEditor slotKey={slot} value={c.names[slot] || ''} label={labelOf(slot)} onChange={(v) => setName(slot, v)}
          onMove={setSlot} onClose={() => setSlot(null)} />
      )}
      <UpgradePrompt kind={prompt} onClose={() => setPrompt(null)} onPrimary={upgrade} onBlank={downloadBlank} blankBusy={blankBusy} />
      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} signedIn={!!user} isPro={isPro} share={sync.share} status={sync.status}
        title={title} templateId={template.id} onCreate={sync.create} onStop={sync.stop} onUpgrade={() => { setShareOpen(false); upgrade(); }} />
      <SaveListDialog open={saveOpen} onOpenChange={setSaveOpen} teams={typedTeams} defaultName={c.title.trim()} />
    </>
  );
});

LibraryBracket.displayName = 'LibraryBracket';
